import { COMMENTED, HEARTED, HIDDEN, MEMO_CREATED, SHARED } from '@careapp-group/core';
import { CommentDto, CommentReportDto, MemoResponseDto } from '@careapp-group/ng-sdk';
import { CAAction } from '../actions';
import { ORGANISATION_CONTEXT_SWITCH } from '../actions/meta.actions';
import { DataListState, INITIAL_DATA_LIST_STATE } from '../data-list.state';
import { Feed } from '../feed/feed.actions';
import { ReducerStateFactory } from '../state-factory.reducer';
import { Memos } from './memos.actions';

export interface MemosStateInterface extends DataListState<MemoResponseDto> {
  loading: boolean;
  actioningEntities: {
    [id: string]: {
      [SHARED]: boolean;
      [HIDDEN]: boolean;
    };
  };
  errorEntities: {
    [id: string]: {
      [SHARED]: boolean;
      [HIDDEN]: boolean;
    };
  };
  reportedCommentIds: string[];
  reportedComments: {
    [commentId: string]: CommentReportDto[];
  };
}

class MemosState extends ReducerStateFactory<MemoResponseDto> {
  readonly initialState: MemosStateInterface = {
    ...INITIAL_DATA_LIST_STATE,
    loading: false,
    actioningEntities: {},
    errorEntities: {},
    reportedCommentIds: [],
    reportedComments: {},
  };

  constructor(actions) {
    super(actions);
  }

  public reducer(state = this.initialState, action: CAAction | any): MemosStateInterface {
    switch (action.type) {
      case ORGANISATION_CONTEXT_SWITCH:
        return { ...state, entities: {}, ids: [], selectedId: null };
      case Memos.requestAddComment.type:
      case Memos.REQUEST_SET_COMMENT_VISIBILITY:
        return { ...state, loading: true };
      case Feed.RESPONSE_SUCCESS_ALL_PAGINATED: {
        if (action.payload.paginator !== 'feed') {
          return state;
        }
        const newEntities = (action.payload.items || [])
          .filter((item) => item.type === MEMO_CREATED)
          .map((item) => item.relatesTo);
        const ids = Array.from(new Set([...state.ids, ...newEntities.map((item) => item.id)]));
        const entities = newEntities.reduce(
          (acc: any, entity: any) => ({
            ...acc,
            [entity.id]: entity,
          }),
          { ...state.entities },
        );
        return { ...state, ids, entities };
      }

      case Memos.instantCreateComment.type: {
        const typedAction: ReturnType<typeof Memos.instantCreateComment> = action;
        const currentEntity = state.entities[typedAction.memoId];
        const comments = currentEntity.comments || [];
        const updatedEntity = {
          ...currentEntity,
          loading: true,
          commentCount: currentEntity.commentCount + 1,
          comments: [
            {
              type: COMMENTED,
              body: typedAction.body,
              modelId: typedAction.memoId,
              memberId: typedAction.memberId,
              organisationId: 'o1',
              tenantId: 't1',
              created: new Date().toISOString(),
              lastUpdated: new Date().toISOString(),
              id: null,
              modelName: 'Memo',
              approved: true,
              deletedAt: null,
              roles: null,
              member: null,
              relevantCustomer: null,
            } as CommentDto,
            ...comments,
          ],
        };
        const entities = {
          ...state.entities,
          [currentEntity.id]: updatedEntity,
        };
        return { ...state, entities };
      }

      case Memos.instantHeart.type: {
        const typedAction: ReturnType<typeof Memos.instantHeart> = action;
        const currentEntity = state.entities[typedAction.memoId];
        if (!currentEntity) {
          return state;
        }
        let actions = currentEntity.actions || [];
        if (typedAction.hearted) {
          actions = [
            {
              type: HEARTED,
              modelId: typedAction.memoId,
              memberId: typedAction.memberId,
              created: new Date().toISOString(),
              lastUpdated: new Date().toISOString(),
              body: null,
              id: null,
              meta: null,
              modelName: 'Memo',
            },
            ...actions,
          ];
        } else {
          actions = actions.filter(
            (_action) => _action.type !== HEARTED && _action.memberId !== typedAction.memberId,
          );
        }

        const updatedEntity = {
          ...currentEntity,
          loading: true,
          hearted: typedAction.hearted,
          heartCount: typedAction.hearted
            ? currentEntity.heartCount + 1
            : currentEntity.heartCount - 1,
          actions,
        };

        const entities = {
          ...state.entities,
          [currentEntity.id]: updatedEntity,
        };
        return { ...state, entities };
      }

      case Memos.RESPONSE_SUCCESS_SET_COMMENT_VISIBILITY: {
        const freeAction = <any>action;
        const payload = freeAction.payload;

        const updatedEntity = {
          ...state.entities[payload.id],
          ...payload,
        };

        const entities = {
          ...state.entities,
          [payload.id]: updatedEntity,
        };

        return { ...state, entities, loading: false };
      }
      case Memos.responseSuccessAddComment.type:
      case Memos.responseSuccessAddHeartAction.type:
      case Memos.responseSuccessLogSavedFileAction.type:
      case Memos.responseSuccessRemoveHeartAction.type: {
        const typedAction: ReturnType<
          | typeof Memos.responseSuccessAddComment
          | typeof Memos.responseSuccessAddHeartAction
          | typeof Memos.responseSuccessLogSavedFileAction
          | typeof Memos.responseSuccessRemoveHeartAction
        > = action;

        const updatedEntity = {
          ...state.entities[typedAction.memo.id],
          ...typedAction.memo,
        };

        const entities = {
          ...state.entities,
          [typedAction.memo.id]: updatedEntity,
        };

        return { ...state, entities, loading: false };
      }
      case Memos.requestCreateActionedAction.type:
      case Memos.responseFailCreateActionedAction.type:
      case Memos.responseSuccessCreateActionedAction.type: {
        const typedAction: ReturnType<
          | typeof Memos.requestCreateActionedAction
          | typeof Memos.responseFailCreateActionedAction
          | typeof Memos.responseSuccessCreateActionedAction
        > = action;
        return updateActioningEntities(state, typedAction);
      }

      case Memos.requestAllPaginatedMemos.type:
      case Memos.responseSuccessAllPaginatedMemos.type:
      case Memos.responseFailAllPaginatedMemos.type: {
        const typedAction: ReturnType<
          | typeof Memos.requestAllPaginatedMemos
          | typeof Memos.responseSuccessAllPaginatedMemos
          | typeof Memos.responseFailAllPaginatedMemos
        > = action;
        return handlePaginationActions(state, typedAction);
      }

      case Memos.responseFailPublicUrl.type: {
        const typedAction: ReturnType<typeof Memos.responseFailPublicUrl> = action;
        return { ...state, error: typedAction.error };
      }

      case Memos.RESPONSE_FAIL_SET_COMMENT_VISIBILITY:
      case Memos.CANCEL_SET_COMMENT_VISIBILITY:
        return { ...state, loading: false };

      // Reporting a comment
      case Memos.requestReportMemoComment.type:
        return { ...state, loading: true };
      case Memos.responseSuccessReportMemoComment.type:
        return addMemoCommentReport(state, action.report as CommentReportDto);
      case Memos.responseFailReportMemoComment.type:
        return { ...state, loading: false, error: action.error };

      // Requesting comment reports
      case Memos.requestAllMemoCommentReports.type:
        return { ...state, loading: true };
      case Memos.responseSuccessAllMemoCommentReports.type: {
        const reports = action.reports as CommentReportDto[];
        return {
          ...state,
          reportedCommentIds: Array.from(new Set([...reports.map((report) => report.commentId)])),
          reportedComments: reports.reduce(
            (acc: any, report: any) => ({
              ...acc,
              [report.commentId]: [...(acc[report.commentId] ?? []), report],
            }),
            {},
          ),
          loading: false,
        };
      }
      case Memos.responseFailAllMemoCommentReports.type:
        return { ...state, loading: false, error: action.error };

      // Deleting comment reports
      case Memos.requestDeleteReportsForComment.type:
        return { ...state, loading: true };
      case Memos.responseSuccessDeleteReportsForComment.type: {
        const commentId = action.commentId as string;
        const reportedCommentIds = state.reportedCommentIds.filter((id) => id !== commentId);
        const reportedComments = { ...state.reportedComments };
        if (reportedComments != null && reportedComments[commentId] != null) {
          delete reportedComments[commentId];
        }

        return {
          ...state,
          reportedCommentIds,
          reportedComments,
          loading: false,
        };
      }
      case Memos.responseFailDeleteReportsForComment.type:
        return { ...state, loading: false, error: action.error };

      // Deleting comments
      case Memos.requestDeleteMemoComment.type:
        return { ...state, loading: true };

      case Memos.responseSuccessDeleteMemoComment.type: {
        // Clear reported comments for this memo comment.
        const commentId = action.commentId as string;
        const reportedCommentIds = state.reportedCommentIds.filter((id) => id !== commentId);
        const reportedComments = { ...state.reportedComments };
        if (reportedComments != null && reportedComments[commentId] != null) {
          delete reportedComments[commentId];
        }

        // Find the memo this comment is a child of.
        const entities = state.entities;
        const memoId = (state.ids as string[]).find((id) =>
          entities[id].comments.some((comment: CommentDto) => comment.id === commentId),
        );
        // Optimistically give that comment a deletedAt property
        const comments = entities[memoId].comments.map((comment: CommentDto) => {
          if (comment.id !== commentId) {
            return comment;
          } else {
            return { ...comment, approved: false, deletedAt: Date.now() };
          }
        });

        const commentCount = (comments ?? []).filter((comment) => comment.deletedAt == null).length;

        const memoEntity = {
          ...entities[memoId],
          comments,
          commentCount: commentCount,
          commented: commentCount > 0,
        } as MemoResponseDto;

        return {
          ...state,
          reportedCommentIds,
          reportedComments,
          loading: false,
          entities: { ...entities, [memoId]: memoEntity },
        };
      }
      case Memos.responseFailDeleteMemoComment.type:
        return { ...state, loading: false, error: action.error };

      // Approving comments
      case Memos.requestApproveMemoComment.type:
        return { ...state, loading: true };
      case Memos.responseSuccessApproveMemoComment.type: {
        const commentId = action.commentId as string;
        const memoIds = state.ids;
        let memoEntities = { ...state.entities };

        const memoIdForComment = memoIds.find(
          (id) => memoEntities[id].comments?.some((comment) => comment.id === commentId) ?? false,
        );

        if (
          memoIdForComment != null &&
          memoEntities[memoIdForComment] != null &&
          memoEntities[memoIdForComment].comments != null &&
          !!memoEntities[memoIdForComment].comments.length
        ) {
          memoEntities[memoIdForComment] = {
            ...memoEntities[memoIdForComment],
            comments: [
              ...(memoEntities[memoIdForComment].comments ?? []).map((comment) => {
                if (comment.id === commentId) {
                  return {
                    ...comment,
                    approved: true,
                  };
                }
                return comment;
              }),
            ],
          };
        }

        return {
          ...state,
          loading: false,
          entities: memoEntities,
        };
      }
      case Memos.responseFailApproveMemoComment.type:
        return { ...state, loading: false, error: action.error };

      default:
        return <any>super.reducer(state, action);
    }
  }
}

function addMemoCommentReport(
  state: MemosStateInterface,
  report: CommentReportDto,
): MemosStateInterface {
  const newIds = Array.from(new Set([...state.reportedCommentIds, report.commentId]));
  // All reports for this comment.
  const commentReports: CommentReportDto[] =
    state.reportedComments[report.commentId] ?? ([] as CommentReportDto[]);
  const newReportedComments = {
    ...state.reportedComments,
    [report.commentId]: [...commentReports, report],
  };
  return {
    ...state,
    reportedCommentIds: newIds,
    reportedComments: newReportedComments,
    loading: false,
  };
}

function updateActioningEntities(
  state,
  action: ReturnType<
    | typeof Memos.requestCreateActionedAction
    | typeof Memos.responseFailCreateActionedAction
    | typeof Memos.responseSuccessCreateActionedAction
  >,
): MemosStateInterface {
  switch (action.type) {
    case Memos.requestCreateActionedAction.type: {
      const typedAction = action as ReturnType<typeof Memos.requestCreateActionedAction>;
      const newState: MemosStateInterface = {
        ...state,
        actioningEntities: {
          ...state.actioningEntities,
          [state.selectedId]: {
            ...(state.actioningEntities[state.selectedId] || {}),
            [typedAction.actionType]: true,
          },
        },
        errorEntities: {
          ...state.errorEntities,
          [state.selectedId]: {
            ...(state.errorEntities[state.selectedId] || {}),
            [typedAction.actionType]: false,
          },
        },
      };
      return newState;
    }
    case Memos.responseSuccessCreateActionedAction.type: {
      const typedAction = action as ReturnType<typeof Memos.responseSuccessCreateActionedAction>;
      const newState: MemosStateInterface = {
        ...state,
        entities: {
          ...state.entities,
          [typedAction.memo.id]: typedAction.memo,
        },
        actioningEntities: {
          ...state.actioningEntities,
          [state.selectedId]: {
            ...(state.actioningEntities[state.selectedId] || {}),
            [typedAction.actionType]: false,
          },
        },
        errorEntities: {
          ...state.errorEntities,
          [state.selectedId]: {
            ...(state.errorEntities[state.selectedId] || {}),
            [typedAction.actionType]: false,
          },
        },
      };
      return newState;
    }
    case Memos.responseFailCreateActionedAction.type: {
      const typedAction = action as ReturnType<typeof Memos.responseFailCreateActionedAction>;
      const newState: MemosStateInterface = {
        ...state,
        actioningEntities: {
          ...state.actioningEntities,
          [typedAction.memoId]: {
            ...(state.actioningEntities[typedAction.memoId] || {}),
            [typedAction.actionType]: false,
          },
        },
        errorEntities: {
          ...state.errorEntities,
          [typedAction.memoId]: {
            ...(state.errorEntities[typedAction.memoId] || {}),
            [typedAction.actionType]: typedAction.error,
          },
        },
      };
      return newState;
    }
  }
}

// :TODO: (T Bowden 13 Apr '23), State factory reducer code uses LoopBackFilter
// in the props for requesting paginated entities. We can no longer provide
// an arbitrary "where" property, so a custom set of paginated actions has been made.
function handlePaginationActions(
  state,
  action: ReturnType<
    | typeof Memos.requestAllPaginatedMemos
    | typeof Memos.responseSuccessAllPaginatedMemos
    | typeof Memos.responseFailAllPaginatedMemos
  >,
): MemosStateInterface {
  switch (action.type) {
    case Memos.requestAllPaginatedMemos.type: {
      const typedAction = action as ReturnType<typeof Memos.requestAllPaginatedMemos>;
      const paginators = state.paginators || {};
      const paginator = {
        [typedAction.paginator]: { ...paginators[action.paginator], loading: true },
      };
      return { ...state, paginators: { ...paginators, ...paginator } };
    }

    case Memos.responseSuccessAllPaginatedMemos.type: {
      const typedAction = action as ReturnType<typeof Memos.responseSuccessAllPaginatedMemos>;
      const { items, filter, paginator } = typedAction;
      const time = new Date().valueOf();
      const updatedAt = items.reduce(
        (acc: any, entity: any) => ({ ...acc, [entity.id]: time }),
        {},
      );
      const entities = items.reduce(
        (acc: any, entity: any) => ({ ...acc, [entity.id]: entity }),
        {},
      );
      const paginators = state.paginators;
      const complete = items.length < filter.limit;
      const skip = filter.skip || 0;
      const ids = state.paginators[paginator] ? state.paginators[paginator].ids || [] : [];
      const paginatorIds = [...ids];

      const newIds = items.map((item) => item.id);
      const allIds = Array.from(new Set([...state.ids, ...newIds]));

      for (let i = 0; i < items.length; i++) {
        paginatorIds[i + skip] = items[i].id;
      }

      return {
        ...state,
        entities: { ...state.entities, ...entities },
        ids: allIds,
        updatedAt: { ...state.updatedAt, ...updatedAt },
        paginators: {
          ...paginators,
          [paginator]: { ids: paginatorIds, complete, loading: false },
        },
      };
    }

    case Memos.responseFailAllPaginatedMemos.type: {
      const typedAction = action as ReturnType<typeof Memos.responseFailAllPaginatedMemos>;
      const paginators = state.paginators;
      const paginator = {
        [typedAction.paginator]: {
          ...paginators[typedAction.paginator],
          loading: false,
          error: typedAction.error,
        },
      };

      return { ...state, paginators: { ...paginators, ...paginator } };
    }
  }
}

export const state = new MemosState(Memos);

export function reducer(...args) {
  return state.reducer.apply(state, args);
}

// We don't want to persist the feed contents, but we do want the key to be there
export function getPersisted(segment: any): any {
  return state.initialState;
}
